import { format, parseISO, startOfMonth, sub } from "date-fns";
import {
	AlertTriangle,
	Clock,
	Pencil,
	TrendingDown,
	TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";

import { useConsumptionData } from "@/api/consumption";
import { useCreateAlert } from "@/api/createAlert";
import { useAlertData } from "@/api/getAlert";
import { useUpdateAlert } from "@/api/updateAlert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfig } from "@/context/use-context";
import { formatCurrency } from "@/lib/format-for-view";

export default function EstimatedCostCard() {
	const { pod } = useConfig();
	const [kwhGoal, setKwhGoal] = useState("");
	const [euroBudget, setEuroBudget] = useState("");
	const [alertsEnabled, setAlertsEnabled] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [unit, setUnit] = useState<"kWh" | "EUR">("kWh");
	const [isEditing, setIsEditing] = useState(false);

	const { mutate: createAlert } = useCreateAlert();
	const { mutate: updateAlert } = useUpdateAlert();

	// Calculate dates for current period
	const twoDaysAgo = sub(new Date(), { days: 2 });
	const startOfCurrentMonth = startOfMonth(twoDaysAgo);
	const dayOfMonthTwoDaysAgo = twoDaysAgo.getDate();

	// Calculate dates for previous month same period
	const startOfPreviousMonth = startOfMonth(
		sub(startOfCurrentMonth, { days: 1 }),
	);
	const endOfPreviousMonthPeriod = sub(startOfPreviousMonth, {
		days: -dayOfMonthTwoDaysAgo + 1,
	});

	// Fetch current month data
	const {
		data: apiData,
		isLoading: isCurrentLoading,
		error: currentError,
	} = useConsumptionData({
		energy: "ELEC",
		granularity: "DAILY",
		start_date: format(startOfPreviousMonth, "yyyy-MM-dd"),
		end_date: format(twoDaysAgo, "yyyy-MM-dd"),
	});

	const { data: alertData, isLoading: isAlertLoading } = useAlertData({
		energy: "ELEC",
	});

	const hasAlerts =
		alertData?.alerts_details?.some(
			(alert) =>
				alert.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
				alert.alert_type === "USAGE_LIMIT_COST_MONTHLY",
		) ?? false;

	const currentAlert =
		alertData?.alerts_details?.find(
			(alert) =>
				alert.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
				alert.alert_type === "USAGE_LIMIT_COST_MONTHLY",
		) ?? null;
	const alertValue = currentAlert?.value ?? null;
	const alertUnit = currentAlert?.unit ?? null;

	const currentMonthData = useMemo(
		() =>
			apiData?.data.filter(
				(item) => parseISO(item.timestamp) >= startOfCurrentMonth,
			),
		[apiData, startOfCurrentMonth],
	);

	const previousMonthData = useMemo(
		() =>
			apiData?.data.filter(
				(item) => parseISO(item.timestamp) <= endOfPreviousMonthPeriod,
			),
		[apiData, endOfPreviousMonthPeriod],
	);

	// Calculate current month totals
	const currentUsage =
		currentMonthData?.reduce(
			(sum: number, item: { consumption: number }) => sum + item.consumption,
			0,
		) ?? 0;

	const currentCost =
		currentMonthData?.reduce(
			(sum: number, item: { cost: number; subscription_cost: number }) =>
				sum + item.cost + item.subscription_cost,
			0,
		) ?? 0;

	// Calculate previous month totals for the same period
	const previousUsage =
		previousMonthData?.reduce(
			(sum: number, item: { consumption: number }) => sum + item.consumption,
			0,
		) ?? 0;

	const previousCost =
		previousMonthData?.reduce(
			(sum: number, item: { cost: number; subscription_cost: number }) =>
				sum + item.cost + item.subscription_cost,
			0,
		) ?? 0;

	// Calculate percentage change if previous month data exists
	const hasPreviousData = previousMonthData && previousMonthData.length > 0;

	let usageChange = 0;
	let costChange = 0;
	let isIncrease = false;

	if (hasPreviousData && previousUsage > 0) {
		usageChange = ((currentUsage - previousUsage) / previousUsage) * 100;
		isIncrease = usageChange > 0;
	}

	if (hasPreviousData && previousCost > 0) {
		costChange = ((currentCost - previousCost) / previousCost) * 100;
		isIncrease = costChange > 0;
	}

	// Calculate total HP and HC consumption for current month
	const totalHp =
		currentMonthData?.reduce(
			(sum: number, item: { consumption_hp: number | null }) =>
				sum + (item.consumption_hp ?? 0),
			0,
		) ?? 0;
	const totalHc =
		currentMonthData?.reduce(
			(sum: number, item: { consumption_hc: number | null }) =>
				sum + (item.consumption_hc ?? 0),
			0,
		) ?? 0;

	// Calculate percentages for HP and HC relative to total usage
	const peakPercentage = currentUsage > 0 ? (totalHp / currentUsage) * 100 : 0;
	const offPeakPercentage =
		currentUsage > 0 ? (totalHc / currentUsage) * 100 : 0;

	// Calculate progress based on the alert unit and value from the API
	const progress =
		alertValue && alertUnit
			? alertUnit === "kWh"
				? (currentUsage / alertValue) * 100
				: alertUnit === "EUR"
					? (currentCost / alertValue) * 100
					: 0
			: 0;

	const exceeded = alertValue && progress > 100;

	const handleOpenDialog = (editing = false) => {
		setIsEditing(editing);
		if (editing && currentAlert) {
			if (currentAlert.unit === "kWh") {
				setKwhGoal(currentAlert.value.toString());
				setEuroBudget("0");
			} else {
				setEuroBudget(currentAlert.value.toString());
				setKwhGoal("");
			}
			setUnit(currentAlert.unit as "kWh" | "EUR");
			setAlertsEnabled(true);
		} else {
			setKwhGoal("");
			setEuroBudget("");
			setUnit("kWh");
			setAlertsEnabled(false);
		}
		setDialogOpen(true);
	};

	const handleSubmit = () => {
		if (isEditing && currentAlert) {
			// Update existing alert
			if (unit === "kWh") {
				updateAlert({
					alert_type: "USAGE_LIMIT_CONSUMPTION_MONTHLY",
					unit: "kWh",
					alert_id: currentAlert.alert_id,
					energy: "ELEC",
					value: Number(kwhGoal),
					is_active: true,
				});
			} else {
				updateAlert({
					alert_type: "USAGE_LIMIT_COST_MONTHLY",
					unit: "EUR",
					alert_id: currentAlert.alert_id,
					energy: "ELEC",
					value: Number(euroBudget),
					is_active: true,
				});
			}
		} else {
			// Create new alert
			createAlert({
				energy: "ELEC",
				alert_type:
					unit === "kWh"
						? "USAGE_LIMIT_CONSUMPTION_MONTHLY"
						: "USAGE_LIMIT_COST_MONTHLY",
				value: unit === "kWh" ? Number(kwhGoal) : Number(euroBudget),
				unit: unit,
			});
		}
		setDialogOpen(false);
	};

	if (isCurrentLoading || isAlertLoading) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	if (currentError) {
		console.error("Error fetching consumption data:", currentError);
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	const onBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// allow float values
		const schema = z
			.number({ coerce: true })
			.max(999999)
			.safeParse(e.target.value);

		if (!schema.data) {
			setKwhGoal("");
			setEuroBudget("");
			return;
		}

		if (unit === "kWh") {
			setKwhGoal(schema.data?.toString() ?? "");
		} else {
			setEuroBudget(schema.data?.toString() ?? "");
		}
	};

	return (
		<Card className="w-full max-w-md">
			<CardContent className="px-3 sm:px-6 py-6">
				{/* Header */}
				<div className="flex justify-between items-center mb-2">
					<Clock className="h-4 w-4 text-gray-400" />
					<p className="text-xs">
						Dernière mise à jour le {format(twoDaysAgo, "dd/MM")}
					</p>
				</div>

				<h3 className="text-base font-medium mb-3">
					Consommation d'énergie du mois en cours
				</h3>

				<div className="flex items-baseline mb-2">
					<span className="text-lg font-bold">
						{currentUsage.toFixed(2)} kWh
					</span>
					<div className="mx-3 h-4 w-px bg-gray-300" />
					<span className="text-lg font-bold">
						{formatCurrency(currentCost)}
					</span>
				</div>

				{hasPreviousData && (
					<div className="flex items-center mb-4 space-x-1">
						<span
							className={`text-sm ${isIncrease ? "text-red-500" : "text-green-500"}`}
						>
							{`${isIncrease ? "+" : "-"}${Math.abs(usageChange).toFixed(0)}% par rapport au mois précédent à la même période`}
						</span>
						{isIncrease ? (
							<TrendingUp className="w-4 h-4 text-red-500" />
						) : (
							<TrendingDown className="w-4 h-4 text-green-500" />
						)}
					</div>
				)}

				{pod?.pricing_option === "HPHC" && (
					<div className="space-y-2 mb-4">
						<div className="flex justify-between">
							<div className="flex items-center">
								<div className="w-3 h-3 bg-teal-400 rounded-sm mr-2" />
								<span className="text-sm">Heures Pleines</span>
							</div>
							<span className="text-sm font-medium">
								{peakPercentage.toFixed(0)}%
							</span>
						</div>
						<div className="flex justify-between">
							<div className="flex items-center">
								<div className="w-3 h-3 bg-pink-600 rounded-sm mr-2" />
								<span className="text-sm">Heures Creuses</span>
							</div>
							<span className="text-sm font-medium">
								{offPeakPercentage.toFixed(0)}%
							</span>
						</div>
					</div>
				)}

				<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
					{/* If no alert is available via API, show "Set Goal" button */}
					{!hasAlerts ? (
						<Button
							className="w-full bg-pink-500 text-white mt-4"
							onClick={() => handleOpenDialog(false)}
						>
							Définir un budget
						</Button>
					) : (
						/* If an alert is available, show the progress bar section */
						<div>
							<div className="flex justify-between items-center mb-2">
								<span className="text-sm font-medium">
									Pourcentage de votre objectif mensuel
								</span>
								<DialogTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9 hover:bg-pink-100"
										onClick={() => handleOpenDialog(true)}
									>
										<Pencil className="h-4 w-4 text-black" />
									</Button>
								</DialogTrigger>
							</div>

							{/* Progress bar using API alert data */}
							<div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
								<div
									className={`${exceeded ? "bg-red-500" : "bg-green-500"} h-full`}
									style={{ width: `${Math.min(progress, 100)}%` }}
								/>
							</div>

							<div className="flex justify-between mt-1 text-xs text-gray-500">
								<div className="mt-1 text-sm font-medium">0%</div>
								<div
									className={`mt-1 text-sm font-medium ${exceeded ? "text-red-500" : "text-green-500"}`}
								>
									{progress.toFixed(0)}%
								</div>
							</div>
						</div>
					)}

					<DialogContent className="sm:max-w-sm w-[95%] max-w-full rounded-2xl gap-0">
						<DialogHeader className="text-start px-2">
							<DialogTitle className="text-lg font-semibold">
								{isEditing ? "Modifier le budget" : "Définir un budget"}
							</DialogTitle>
						</DialogHeader>

						<div className="space-y-4 p-2">
							<Tabs defaultValue={unit} className="w-full mb-4">
								<TabsList className="w-full grid grid-cols-2">
									<TabsTrigger value="kWh" onClick={() => setUnit("kWh")}>
										kWh
									</TabsTrigger>
									<TabsTrigger value="EUR" onClick={() => setUnit("EUR")}>
										Euro
									</TabsTrigger>
								</TabsList>
							</Tabs>
							<div className="space-y-2">
								<label
									htmlFor="budget"
									className="block text-sm font-medium text-gray-700"
								>
									{unit === "kWh" ? "Consommation (kWh)" : "Budget (€)"}
								</label>
								<div className="relative">
									<Input
										id="budget"
										type="number"
										inputMode="decimal"
										value={unit === "kWh" ? kwhGoal : euroBudget}
										onChange={onBudgetChange}
										placeholder={unit === "kWh" ? "0.0" : "0.00"}
										className="w-full pr-12"
									/>
									<span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
										{unit}
									</span>
								</div>
							</div>

							{/* Alerts Toggle */}
							<div className="flex items-center space-x-2">
								<Switch
									checked={alertsEnabled}
									onCheckedChange={setAlertsEnabled}
								/>
								<p className="text-sm">Activer les alertes de dépassement</p>
							</div>

							<p className="text-sm text-gray-500">
								Vous serez alerté lorsque votre budget dépassera 90%, 100% et
								150% de la limite définie
							</p>

							<Button
								className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-lg py-2"
								onClick={handleSubmit}
							>
								Enregistrer
							</Button>
						</div>
					</DialogContent>
				</Dialog>

				{/* Alert if exceeded */}
				{exceeded && (
					<div className="rounded-md border border-red-300 bg-red-50 p-3 flex items-start mt-4">
						<AlertTriangle className="h-5 w-5 text-red-600" />
						<div className="ml-3">
							<h4 className="text-sm font-semibold text-red-600">Alerte</h4>
							<p className="text-sm text-red-600 mt-1">
								Votre limite de consommation a été dépassée.
							</p>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
